import React from 'react';
/* eslint-disable-next-line import/no-extraneous-dependencies */
// Note: lucide-react has been installed via npm install lucide-react
import {
  // Search and UI icons
  Search, X, Grid3X3,
  // CRM related icons
  Users, User, UserPlus, Building, Phone, Calendar, Bell, BellRing,
  // Document icons
  FileText, Files, ClipboardList, ClipboardCheck, FileSpreadsheet, 
  // Business icons
  Briefcase, BarChart, PieChart, TrendingUp, DollarSign, CreditCard, Wallet,
  // Communication icons
  MessageSquare, MessageCircle, Mail, Send, Share, 
  // Settings and tools
  Settings, Sliders, Filter, 
  // Status icons
  CheckCircle, XCircle, AlertCircle, Clock, Activity,
  // Other useful icons
  Star, Heart, Bookmark, Tag, Link, Globe, Image
} from 'lucide-react';

type ViewIconDisplayProps = {
  iconType?: string;
  iconKey?: string;
  // iconAssetId is reserved for future use with custom uploaded icons
  // Removing the unused prop to fix the lint error
  className?: string;
  size?: number;
};

/**
 * Component to display view icons in the sidebar and other places
 * Handles both emoji and icon types from the built-in library
 */
const ViewIconDisplay: React.FC<ViewIconDisplayProps> = ({
  iconType,
  iconKey,
  className = '',
  size = 20
}) => {
  // Map of available icons - must match all icons in IconPicker component
  const iconMap: Record<string, any> = {
    // Search and UI icons
    Search,
    X,
    Grid3X3,
    // CRM related icons
    Users,
    User,
    UserPlus,
    Building,
    Phone,
    Calendar,
    Bell,
    BellRing,
    // Document icons
    FileText,
    Files,
    ClipboardList,
    ClipboardCheck,
    FileSpreadsheet,
    // Business icons
    Briefcase,
    BarChart,
    PieChart,
    TrendingUp,
    DollarSign,
    CreditCard,
    Wallet,
    // Communication icons
    MessageSquare,
    MessageCircle,
    Mail,
    Send,
    Share,
    // Settings and tools
    Settings,
    Sliders,
    Filter,
    // Status icons
    CheckCircle,
    XCircle,
    AlertCircle,
    Clock,
    Activity,
    // Other useful icons
    Star,
    Heart,
    Bookmark,
    Tag,
    Link,
    Globe,
    Image
  };

  // Check if the iconKey is an emoji (typically Unicode characters)
  const isEmoji = (text?: string) => {
    if (!text) return false;
    // Simple check: emojis are typically outside the ASCII range
    // This will detect actual emoji characters
    return Array.from(text).some(char => {
      const codePoint = char.codePointAt(0);
      return codePoint && codePoint > 127;
    });
  };

  const resolveIconName = (rawName?: string) => {
    if (!rawName) return undefined;
    if (iconMap[rawName]) return rawName;

    const aliasMap: Record<string, string> = {
      grid: "Grid3X3",
      grid3x3: "Grid3X3",
      activity: "Activity",
      "trending-up": "TrendingUp",
      "alert-circle": "AlertCircle",
      "user-plus": "UserPlus",
    };
    const alias = aliasMap[rawName.trim().toLowerCase()];
    if (alias && iconMap[alias]) return alias;

    const normalized = rawName
      .trim()
      .replace(/[-_\s]+(.)?/g, (_, ch) => (ch ? ch.toUpperCase() : ""))
      .replace(/^(.)/, (ch) => ch.toUpperCase());
    if (iconMap[normalized]) return normalized;

    const caseInsensitiveMatch = Object.keys(iconMap).find(
      (key) => key.toLowerCase() === rawName.toLowerCase(),
    );
    return caseInsensitiveMatch;
  };

  const parseIconPayload = (raw?: string) => {
    if (!raw || typeof raw !== "string" || !raw.trim().startsWith("{")) return null;
    try {
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return null;
      return {
        type: parsed.type,
        value: parsed.value || parsed.icon_key || parsed.key,
      };
    } catch {
      return null;
    }
  };

  const parsedIcon = parseIconPayload(iconKey);
  const resolvedIconKey = parsedIcon?.value || iconKey;
  const resolvedType = parsedIcon?.type
    ? (parsedIcon.type === "emoji" ? "custom" : "built_in")
    : iconType;

  if (resolvedIconKey) {
    if (isEmoji(resolvedIconKey)) {
      return (
        <span 
          className={`flex items-center justify-center ${className}`}
          style={{ fontSize: Math.max(size * 0.8, 16) }}
        >
          {resolvedIconKey}
        </span>
      );
    }

    const iconName = resolveIconName(resolvedIconKey);
    if (iconName) {
      const IconComponent = iconMap[iconName];
      return <IconComponent className={className} size={size} />;
    }
  }

  if (resolvedType === "custom" && parsedIcon?.value) {
    return (
      <span 
        className={`flex items-center justify-center ${className}`}
        style={{ fontSize: Math.max(size * 0.8, 16) }}
      >
        {parsedIcon.value}
      </span>
    );
  }
  
  // For custom icons (future implementation) or fallback
  return (
    <div className={`flex items-center justify-center ${className}`}>
      <Building size={size} className="text-gray-400" />
    </div>
  );
};

export default ViewIconDisplay;
